import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button, 
  Dialog,
  Box,
  Chip,
  Stack
} from '@mui/material';
import { Link } from 'react-router-dom';
import BangladeshPayment from './BangladeshPayment';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import PaymentModal from './PaymentModal';

const StyledCard = styled(motion.div)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
}));

const PriceChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  padding: '8px 4px',
  '& .MuiChip-label': {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

export const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function CarCard({ vehicle }) {
  const [showPayment, setShowPayment] = useState(false);

  const handleBookNow = () => {
    setShowPayment(true);
  };

  const handlePaymentComplete = (paymentDetails) => {
    // Handle successful payment
    console.log('Payment completed:', paymentDetails);
    setShowPayment(false);
    // Navigate to confirmation page or show success message
  };

  // Format price based on rental type (per hour or per day)
  const formatPrice = () => {
    const rate = vehicle.type === 'bike' ? '/hour' : '/day';
    return `৳${vehicle.price}${rate}`;
  };

  return (
    <>
      <StyledCard
        whileHover={{ 
          y: -10,
          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
        }}
        transition={{ duration: 0.2 }}
      >
        <CardMedia
          component="img"
          image={vehicle.image}
          alt={vehicle.name}
          sx={{ height: 200 }}
        />
        <PriceChip label={formatPrice()} />
        <CardContent sx={{ flexGrow: 1, pt: 3 }}>
          <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {vehicle.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {vehicle.type === 'bike' ? (
              // Bike specifications
              <>
                <Chip 
                  size="small" 
                  label={`${vehicle.engineSize || ''}cc`}
                  sx={{ backgroundColor: 'rgba(139, 0, 0, 0.08)' }}
                />
                <Chip 
                  size="small" 
                  label={`${vehicle.power || ''} BHP`}
                  sx={{ backgroundColor: 'rgba(139, 0, 0, 0.08)' }}
                />
              </>
            ) : (
              // Car specifications
              <>
                <Chip 
                  size="small" 
                  label={vehicle.transmission || 'Automatic'} 
                  sx={{ backgroundColor: 'rgba(139, 0, 0, 0.08)' }}
                />
                <Chip 
                  size="small" 
                  label={vehicle.fuelType || 'Petrol'} 
                  sx={{ backgroundColor: 'rgba(139, 0, 0, 0.08)' }}
                />
              </>
            )}
          </Stack>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              onClick={handleBookNow}
              fullWidth
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              Book Now
            </Button>
            <Button 
              component={Link} 
              to={`/vehicles/${vehicle.id}`}
              variant="outlined"
              fullWidth
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'rgba(139, 0, 0, 0.04)',
                }
              }}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </StyledCard>

      <PaymentModal
        open={showPayment}
        onClose={() => setShowPayment(false)}
        bookingDetails={{
          vehicleName: vehicle.name,
          totalAmount: vehicle.price,
          vehicleId: vehicle.id
        }}
        onPaymentComplete={handlePaymentComplete}
      />
    </>
  );
}

export default CarCard; 